import { AddressItemType } from '@@types'
import { nanoid } from 'nanoid'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ReactSVG } from 'react-svg'

import i18n from '@app/i18n'

import { type Brand, CustomerAddressInput, useAddressStore, useLazyFetchAddressSuggestions, useMutateSaveAddress } from '@entities/Address'

import { useAuth } from '@features/auth'
import { useErrorInfoModal, useModalStore } from '@features/modal'
import { useDelivery } from '@features/shipping-methods/hooks/useDelivery'
import SelectBrandModal from '@features/shipping-methods/ui/SelectBrandModal'

import { useSlots } from '@widgets/slots'

import { SHOP_ID } from '@shared/contants'
import { useYandexSuggest } from '@shared/hooks/useYandexSuggest'
import Button from '@shared/ui/button'
import LoaderCircle from '@shared/ui/loader-circle'
import Search from '@shared/ui/search'

import { AddressList } from '../AddressList'
import AddressForm, { FormValues } from './AddressForm'
import DeliveryMap from './DeliveryMap'
import RequestDeliveryForm from './RequestDeliveryForm'

import styles from './DeliveryView.module.scss'


type ActiveTab = 'map' | 'newAddress' | null;
interface GeoPoint {
  latitude: number;
  longitude: number;
}

type Props = {
  isDesktop: boolean;
  setHeaderConfig: React.Dispatch<
    React.SetStateAction<{
      title: string;
      icon?: boolean;
      onClick?: () => void;
      isTabs?: boolean;
    }>
  >;
};

export default function Delivery ({ isDesktop, setHeaderConfig }: Props) {
  const { openErrorInfoModal } = useErrorInfoModal()
  const { t } = useTranslation('modalDelivery')
  const { t: ti } = useTranslation('info')
  const { t: to } = useTranslation('order')
  const { t: tc } = useTranslation('common')
  const { t: te } = useTranslation('errors')
  const { 
    setGeoPoint: setStoreGeoPoint,
    setSelectedAddress,
    setDeliveryType,
    setShopId,
    selectedAddress,
    loadingCheckAddressGeopoint,
    currentAddress,
    setCurrentAddress,
    fetchAddressForGeopoint,
  } = useAddressStore()
  const [ saveAddressMutation ] = useMutateSaveAddress()
  const [ _, setIsInvalidZoneBySuggest ] = useState(false)
  const [ activeTab, setActiveTab ] = useState<ActiveTab>(null)
  const [ editingAddress, setEditingAddress ] = useState<AddressItemType | null>(null)
  
  // Инициализируем geoPoint из выбранного адреса если есть координаты
  const initialGeoPoint = useMemo(() => {
    const gp = selectedAddress?.customerGeoPoint
    if (gp?.latitude && gp?.longitude) {
      return { latitude: gp.latitude, longitude: gp.longitude }
    }
    return null
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  
  const [ geoPoint, setGeoPoint ] = useState<GeoPoint | null>(initialGeoPoint)

  const { closeModal, openModal } = useModalStore()
  const { isAuthorized } = useAuth()

  const { fetchSuggestions } = useLazyFetchAddressSuggestions()
  
  // Используем новый хук useDelivery для управления зонами и брендами
  const {
    availableBrands,
    selectBrand,
    deliveryZones,
    loadingZones,
    brand,
  } = useDelivery()
  
  const {
    query: searchInputAddress,
    setQuery: setSearchInputAddress,
    setQueryWithoutFetch,
    suggestions,
    clearSuggestions,
  } = useYandexSuggest({
    apiKey: import.meta.env.VITE_YANDEX_MAPS_API_KEY_SEARCH,
    lang: i18n.language === 'kk' ? 'kk_KZ' : 'ru_RU',
    bbox: import.meta.env.VITE_MAP_SUGGEST_BBOX,
  })

  // Для гостя сразу открываем форму редактирования адреса
  // Если есть сохранённый адрес — заполняем форму его данными
  const hasInitializedGuestAddress = useRef(false)
  
  useEffect(() => {
    if (!isAuthorized) {
      setActiveTab('newAddress')
      
      // Если у анонима уже есть сохранённый адрес — заполняем форму (только один раз)
      if (selectedAddress && selectedAddress.address && !selectedAddress.shop_id && !hasInitializedGuestAddress.current) {
        hasInitializedGuestAddress.current = true
        
        // ВАЖНО: сначала синхронизируем поле поиска, потом устанавливаем currentAddress
        // иначе useEffect сбросит currentAddress в null (см. строку ~165)
        setQueryWithoutFetch(selectedAddress.address)
        // Используем setTimeout чтобы дать React обновить searchInputAddress
        setTimeout(() => {
          setCurrentAddress(selectedAddress.address ?? null)
        }, 0)
        
        const gp = selectedAddress.customerGeoPoint
        if (gp?.latitude && gp?.longitude) {
          const coords = { latitude: gp.latitude, longitude: gp.longitude }
          setGeoPoint(coords)
          setStoreGeoPoint(coords)
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ isAuthorized, selectedAddress ])
  
  // Проверяем есть ли зоны доставки (null = ещё не загружено/загружается, empty = загружено, но нет зон)
  const CORRECT_ZONE_DELIVERY = useMemo(() => {
    // Пока загружаются зоны — не показываем ни ошибку, ни форму
    if (loadingZones) return null
    // Если нет geoPoint и нет зон — ещё ничего не выбрано
    if (!geoPoint && deliveryZones.length === 0) return null
    return deliveryZones.length > 0
  }, [ deliveryZones.length, geoPoint, loadingZones ])
  
  const { openSlotsModal } = useSlots()
  
  // Определяем, редактируем ли существующий адрес (для заголовка)
  // Для авторизованных — проверяем editingAddress, для гостей — selectedAddress
  const isEditingExistingAddress = editingAddress !== null || 
    (!isAuthorized && selectedAddress?.address && !selectedAddress?.shop_id)
  
  useEffect(() => {
    if (activeTab === 'newAddress') {
      setHeaderConfig({
        title: isEditingExistingAddress ? t('edit-address') : t('new-address'),
        icon: isAuthorized,
        onClick: isAuthorized ? () => {
          setActiveTab(null)
          setEditingAddress(null) // Сбрасываем редактируемый адрес при возврате
        } : undefined,
        isTabs: false,
      })
    }
    else {
      setHeaderConfig({ title: t('address'), isTabs: true })
    }
    return () => {
      setHeaderConfig({ title: t('address'), isTabs: true })
    }
  }, [ activeTab, setHeaderConfig, t, isAuthorized, isEditingExistingAddress ])

  useEffect(() => {
    if (currentAddress && searchInputAddress !== currentAddress) {
      setCurrentAddress(null)
      clearSuggestions()
    }
  }, [ searchInputAddress, currentAddress, setCurrentAddress, clearSuggestions ])

  const handleButtonChange = useCallback(() => {
    setEditingAddress(null) // Сбрасываем редактируемый адрес — создаём новый
    setActiveTab('newAddress')
  }, [])

  const handleEditAddress = useCallback((address?: AddressItemType) => {
    if (!address) return
    
    setEditingAddress(address)
    
    // Заполняем форму данными редактируемого адреса
    if (address.address) {
      setQueryWithoutFetch(address.address)
      setTimeout(() => {
        setCurrentAddress(address.address ?? null)
      }, 0)
    }
    
    // Устанавливаем координаты на карте
    const gp = address.customerGeoPoint
    if (gp?.latitude && gp?.longitude) {
      const coords = { latitude: gp.latitude, longitude: gp.longitude }
      setGeoPoint(coords)
      setStoreGeoPoint(coords)
    }
    
    setActiveTab('newAddress')
  }, [ setQueryWithoutFetch, setCurrentAddress, setStoreGeoPoint ])

  const checkAddressForGeoPoint = async ({
    latitude,
    longitude,
  }: {
    latitude: number;
    longitude: number;
  }) => {
    const coords = { latitude, longitude }
    setGeoPoint(coords)
    // Обновляем geoPoint в сторе — useDelivery подхватит и загрузит зоны
    setStoreGeoPoint(coords)

    await fetchAddressForGeopoint(coords, (address) => {
      setSearchInputAddress(address)
      setActiveTab('newAddress')
    })
  }

  useEffect(() => {
    if (
      activeTab === 'newAddress' &&
      searchInputAddress &&
      suggestions.length === 0
    ) {
      setIsInvalidZoneBySuggest(true)
    }
  }, [ suggestions, searchInputAddress, activeTab ])

  useEffect(() => {
    if (searchInputAddress) {
      setIsInvalidZoneBySuggest(false)
    }
  }, [ searchInputAddress ])

  // Открыть модал выбора бренда
  const openBrandSelectModal = useCallback(() => {
    openModal(
      'selectBrand',
      <SelectBrandModal
        brands={availableBrands}
        selectedBrand={brand}
        onSelect={(selectedBrand: Brand) => {
          selectBrand(selectedBrand)
          setDeliveryType('delivery')
          closeModal('delivery')
          openSlotsModal()
        }}
      />,
      {
        title: tc('selectBrand'),
        description: tc('chooseBrandForOrder'),
      },
    )
  }, [ availableBrands, brand, selectBrand, openModal, closeModal, openSlotsModal, setDeliveryType, tc ])

  // Обработка результата получения зон - показываем выбор бренда если нужно
  useEffect(() => {
    if (deliveryZones.length > 1 && selectedAddress && !brand && !loadingZones) {
      // Если несколько зон и бренд не выбран - показываем модал выбора
      openBrandSelectModal()
    }
    // Синхронизация зон с shopId делается автоматически в useDelivery
  }, [ deliveryZones.length, selectedAddress, brand, loadingZones, openBrandSelectModal ])

  const handleSaveAddress = async (formValues: FormValues) => {
    try {
      if (!geoPoint || !currentAddress) return

      const address: CustomerAddressInput = {
        // Если редактируем существующий адрес — передаём его id
        ...(editingAddress?.id ? { id: editingAddress.id } : {}),
        address: currentAddress,
        address_appartments: formValues.apartment ?? '',
        address_floor: formValues.floor ?? '',
        address_porch: formValues.entrance ?? '',
        address_intercom: formValues.intercom ?? '',
        comment: formValues.comment ?? '',
        other_recipient: formValues.otherBuyer ?? false,
        recipient_name: formValues.otherBuyerName ?? '',
        recipient_phone: formValues.otherBuyerPhone ?? '',
        recipient_last_name: '',
        door_delivery: true,
        customerGeoPoint: geoPoint,
        label: formValues.title ?? '',
      }

      const guestId = editingAddress?.id ?? selectedAddress?.id ?? `guest-${nanoid()}`

      if (isAuthorized) {
        const { data } = await saveAddressMutation({
          variables: { addressInfo: address },
        })
        
        if (data?.saveAddress) {
          setSelectedAddress({
            ...data.saveAddress,
            customerGeoPoint: geoPoint,
          } as AddressItemType)
        }
      }
      else {
        // Для анонима просто сохраняем в локальный стейт
        setSelectedAddress({
          ...address,
          id: guestId,
          door_delivery: true,
        } as AddressItemType)
      }

      // Сбрасываем редактируемый адрес после сохранения
      setEditingAddress(null)

      // Обновляем geoPoint в сторе для запроса зон через useDelivery
      setStoreGeoPoint(geoPoint)
      setDeliveryType('delivery')
      setActiveTab(null)

      // useDelivery автоматически получит зоны
      // Если несколько брендов - откроется модал выбора через useEffect
      // Если один бренд - зона установится автоматически
      if (availableBrands.length > 1) {
        // Модал откроется через useEffect выше
      }
      else if (availableBrands.length === 1) {
        const zone = deliveryZones[0]
        if (zone?.shop_id) {
          setShopId(zone.shop_id)
        }
        closeModal('delivery')
        openSlotsModal()
      }
      else if (deliveryZones.length > 0) {
        const foundShopId = deliveryZones[0]?.shop_id
        if (foundShopId) {
          setShopId(foundShopId)
          closeModal('delivery')
          openSlotsModal()
        }
        else {
          setShopId(SHOP_ID)
        }
      }
      else {
        setShopId(SHOP_ID)
      }
    }
    catch (error) {
      console.error('Error while saving address:', error)
      openErrorInfoModal({
        description: te('save_address'),
      })
    }
  }

  const handleSuggestionsClick = async (text: string) => {
    const res = await fetchSuggestions({ variables: { address: text } })
    const suggestion = res.data?.getAddressSuggestions?.suggestions?.[0]
    const latitude = suggestion?.data?.latitude
    const longitude = suggestion?.data?.longitude
    if (latitude && longitude) {
      checkAddressForGeoPoint({ latitude: +latitude, longitude: +longitude })
    }
  }

  return (
    <div className={styles.root__container}>
      {(loadingZones || loadingCheckAddressGeopoint) && (
        <div className={styles.root__loading}>
          <LoaderCircle className={styles['root__loading-circle']} />
        </div>
      )}

      <div className={styles.root__content}>
        {activeTab === 'newAddress' && (
          <>
            <div className={styles.searchWrapper}>
              <Search
                value={currentAddress || searchInputAddress}
                handleChange={setSearchInputAddress}
                placeholder={t('input.placeholder-delivery')}
              />
              {!currentAddress && suggestions.length > 0 && (
                <div className={styles.suggestionsDropdown}>
                  {suggestions.map((el, i) => (
                    <div
                      key={`${el.title}-${i}`}
                      onClick={() => {
                        handleSuggestionsClick(
                          el.title.text + ` ${el.subtitle?.text}`,
                        )
                      }}
                      className={styles.foundAddress}
                    >
                      <span className={styles.foundAddress__title}>
                        {el.title.text}
                      </span>
                      {el.subtitle?.text && (
                        <span className={styles.foundAddress__subtitle}>
                          , {el.subtitle?.text}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            {!isDesktop && (
              <span
                onClick={() => setActiveTab('map')}
                className={styles.root__touchMap}
              >
                <ReactSVG src="/icons/map-pin.svg" wrapper="span" />
                {t('button.touch-map')}
              </span>
            )}
            {CORRECT_ZONE_DELIVERY === false && currentAddress && (
              <RequestDeliveryForm
                address={currentAddress}
                geoPoint={geoPoint}
                onSuccess={() => closeModal('delivery')}
              />
            )}
            {CORRECT_ZONE_DELIVERY && currentAddress && (
              <AddressForm
                onSubmit={handleSaveAddress}
                isZone={true}
                initialValues={isEditingExistingAddress ? {
                  apartment: (editingAddress ?? selectedAddress)?.address_appartments ?? '',
                  floor: (editingAddress ?? selectedAddress)?.address_floor ?? '',
                  entrance: (editingAddress ?? selectedAddress)?.address_porch ?? '',
                  intercom: (editingAddress ?? selectedAddress)?.address_intercom ?? '',
                  comment: (editingAddress ?? selectedAddress)?.comment ?? '',
                  title: (editingAddress ?? selectedAddress)?.label ?? '',
                  otherBuyer: (editingAddress ?? selectedAddress)?.other_recipient ?? false,
                  otherBuyerName: (editingAddress ?? selectedAddress)?.recipient_name ?? '',
                  otherBuyerPhone: (editingAddress ?? selectedAddress)?.recipient_phone ?? '',
                } : undefined}
                editingAddressId={editingAddress?.id ? String(editingAddress.id) : undefined}
              />
            )}
          </>
        )}
        {activeTab === null && (
          <>
            <p className={styles.content__desc}>{t('desc')}</p>
            <Button
              onClick={handleButtonChange}
              type="submit"
              theme="secondary"
              size={isDesktop ? 'large' : 'medium'}
            >
              {t('button.address')}
            </Button>
            <AddressList onEditAddress={handleEditAddress} />
          </>
        )}
      </div>

      {!isDesktop && activeTab === 'map' && CORRECT_ZONE_DELIVERY === false && (
        <div className={styles.correct}>{to('address.invalid_zone')}</div>
      )}
      {!isDesktop && activeTab === 'map' && (
        <div onClick={() => setActiveTab(null)} className={styles.btn}>
          <ReactSVG
            src="/icons/list.svg"
            width={16}
            height={16}
            wrapper="span"
          />
          {ti('location.button')}
        </div>
      )}
      {(isDesktop || activeTab === 'map') && (
        <DeliveryMap
          newAddress={activeTab === 'newAddress' || !isDesktop}
          onChangeAddressMap={checkAddressForGeoPoint}
          geoPoint={geoPoint}
        />
      )}
    </div>
  )
}
